// hooks/useIndexedDB.ts
import { useState, useEffect } from 'react';

export const useIndexedDB = (
  dbName: string,
  storeName: string,
  isChase: boolean
) => {
  const [db, setDb] = useState<IDBDatabase | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Инициализация БД
  useEffect(() => {
    if (!isChase) {
      return;
    }
    const request = indexedDB.open(dbName, 1);

    request.onerror = (event) => {
      // console.error('Ошибка открытия БД:', event);
    };

    request.onsuccess = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      setDb(database);
      setIsReady(true);
      // console.log('✅ IndexedDB готова');
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      if (!database.objectStoreNames.contains(storeName)) {
        database.createObjectStore(storeName, { keyPath: 'id' });
        // console.log('📁 Создано хранилище:', storeName);
      }
    };
  }, [dbName, storeName]);

  // Функция записи данных
  const setItem = async (id: string, data: any): Promise<void> => {
    if (!db) throw new Error('База данных не инициализирована');

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put({ id, data, timestamp: Date.now() });

      request.onsuccess = () => {
        /*
        console.log(`✅ Запись успешна: ${id}`, {
          dataSize: JSON.stringify(data).length,
          timestamp: new Date().toISOString(),
        });
        */
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  };

  // Функция чтения данных
  const getItem = async (id: string): Promise<any> => {
    if (!db) throw new Error('База данных не инициализирована');

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        /*
        console.log(`✅ Чтение данных: ${id}`, {
          dataSize: JSON.stringify(request).length,
          timestamp: new Date().toISOString(),
        });
        */
        const { result } = request;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  };

  // Функция удаления данных
  const removeItem = async (id: string): Promise<void> => {
    if (!db) throw new Error('База данных не инициализирована');

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  };

  // Функция очистки всех данных
  const clearAll = async (): Promise<void> => {
    if (!db) throw new Error('База данных не инициализирована');

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  };

  return { isReady, setItem, getItem, removeItem, clearAll };
};
